import type { TimelineTrackItem } from "./TimelineTrackItem";
import { DEFAULT_SPAN, type TimeWindow } from "./window";

/** One slice of the history and how much of it was active. */
export interface ActivityBucket {
  startTime: number;
  endTime: number;
  /** Milliseconds of item time inside the bucket; overlapping items add up, so it can exceed the bucket's length. */
  activeMs: number;
}

/**
 * The device's session history, bucketed.
 */
export interface Activity {
  extent: TimeWindow;
  /** How many items the history holds; with none the timeline shows its `empty` slot. */
  itemCount: number;
  buckets: readonly ActivityBucket[];
}

export interface ActivityRequest {
  /** How many equal slices to split the whole history into. */
  bucketCount: number;
}

export type GetActivity = (
  request: ActivityRequest,
  signal: AbortSignal,
) => Promise<Activity>;

/**
 * How long a zero-length item (start and end in the same millisecond) counts
 * as: it has a bar on the tracks, so the strip marks its time as active too.
 */
const POINT_ITEM_MS = 1;

/**
 * Split `extent` into `bucketCount` equal buckets and add each item's overlap
 * to the buckets it touches. Any touched bucket ends up with `activeMs > 0`,
 * so a tiny item still shows. Pure: items are read, never changed.
 */
export function bucketActivity<T>(
  items: readonly TimelineTrackItem<T>[],
  extent: TimeWindow,
  bucketCount: number,
): ActivityBucket[] {
  const width = (extent.end - extent.start) / bucketCount;
  const buckets: ActivityBucket[] = Array.from(
    { length: bucketCount },
    (_, i) => ({
      startTime: extent.start + i * width,
      endTime: extent.start + (i + 1) * width,
      activeMs: 0,
    }),
  );
  if (!(width > 0)) {
    return buckets;
  }
  for (const item of items) {
    const endTime = Math.max(item.endTime, item.startTime + POINT_ITEM_MS);
    const first = Math.max(
      0,
      Math.floor((item.startTime - extent.start) / width),
    );
    const last = Math.min(
      bucketCount - 1,
      Math.ceil((endTime - extent.start) / width) - 1,
    );
    for (let i = first; i <= last; i += 1) {
      const bucket = buckets[i];
      const overlap =
        Math.min(endTime, bucket.endTime) -
        Math.max(item.startTime, bucket.startTime);
      if (overlap > 0) {
        bucket.activeMs += overlap;
      }
    }
  }
  return buckets;
}

/**
 * The activity of `items`, computed client-side: the extent runs from the
 * earliest item's start to `now` (the last `DEFAULT_SPAN` when there are no
 * items), split into `bucketCount` buckets. `now` is the caller's, so the
 * extent's right edge and the opening window agree.
 */
export function activityOf<T>(
  items: readonly TimelineTrackItem<T>[],
  bucketCount: number,
  now: number,
): Activity {
  let start = now - DEFAULT_SPAN;
  if (items.length > 0) {
    start = Infinity;
    for (const item of items) {
      start = Math.min(start, item.startTime);
    }
  }
  const extent = { start, end: now };
  return {
    extent,
    itemCount: items.length,
    buckets: bucketActivity(items, extent, bucketCount),
  };
}
